import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Check, ArrowRight, Car } from "lucide-react";
import { usePageSeo } from "@/hooks/usePageSeo";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageHero from "@/components/PageHero";
import heroImg from "@/assets/hero-fleet.jpg";

interface Vehicle {
  id: string; name: string; capacity: number; min_passengers: number;
  description: string | null; image_url: string | null;
  badge: string | null; features: string[] | null;
}

const FleetPage = () => {
  usePageSeo("fleet");
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.from("vehicles")
      .select("id,name,capacity,min_passengers,description,image_url,badge,features,sort_order")
      .eq("is_active", true)
      .order("sort_order")
      .then(({ data }) => {
        setVehicles((data as Vehicle[]) || []);
        setLoading(false);
      });
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <PageHero
        eyebrow="Our fleet"
        title={<>Vehicles built for <span className="italic text-gradient-ocean">comfort.</span></>}
        subtitle="From private sedans to large vans, every vehicle in our fleet is late-model, fully insured, and meticulously maintained."
        image={heroImg}
      />

      <section className="container mx-auto px-4 py-20">
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 3 }).map((_, i) => <div key={i} className="aspect-[4/3] rounded-3xl bg-muted animate-pulse" />)}
          </div>
        ) : vehicles.length === 0 ? (
          <div className="text-center py-20 max-w-md mx-auto">
            <Car className="w-12 h-12 mx-auto text-primary/60" />
            <h3 className="font-display text-2xl font-semibold mt-5">Fleet details coming soon</h3>
            <p className="text-muted-foreground mt-2">We're adding photos and specs of every vehicle. Check back shortly.</p>
            <Link to="/transfers" className="inline-flex items-center gap-2 mt-6 px-6 py-3 rounded-full bg-gradient-ocean text-primary-foreground font-semibold shadow-glow hover:-translate-y-0.5 transition-smooth">
              Book a transfer <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {vehicles.map((v) => (
              <div key={v.id} className="group bg-card rounded-3xl overflow-hidden shadow-card border border-border/60 hover:shadow-elegant transition-smooth">
                <div className="aspect-[4/3] bg-muted overflow-hidden">
                  {v.image_url ? (
                    <img src={v.image_url} alt={v.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-smooth duration-700" />
                  ) : (
                    <div className="w-full h-full bg-gradient-ocean" />
                  )}
                </div>
                <div className="p-6">
                  {v.badge && <span className="inline-block text-[10px] uppercase tracking-[0.2em] text-accent font-semibold">{v.badge}</span>}
                  <h3 className="font-display text-2xl font-semibold mt-1">{v.name}</h3>
                  <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-2">
                    <Users className="w-4 h-4 text-primary" /> {v.min_passengers}–{v.capacity} passengers
                  </div>
                  {v.description && <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{v.description}</p>}
                  {v.features && v.features.length > 0 && (
                    <ul className="mt-4 space-y-1.5">
                      {v.features.slice(0, 4).map((f, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm">
                          <Check className="w-4 h-4 text-primary" /> {f}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
};

export default FleetPage;
